import React, {
  useCallback,
  useState,
  ReactNode,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { Box, Checkbox } from '@mui/material';
import { BaseMenuItem } from './BaseMenuItem';
import { useOptimisticBoolean } from './hooks/use-optimistic-boolean.hook';
import { BaseMenu, BaseMenuProps } from './BaseMenu';
import { Icon } from '@ui/Icon';
import { useMenuContext } from './MenuContext';
import { MenuGroupContext, useMenuGroupContext } from './MenuGroupContext';
import { SUBMENU_TIMEOUTS, MenuParentContext } from './menu.model';
import { MenuItemRightIcon } from './MenuItemRightIcon';
import { is } from '@client/helpers/dom.guards';

export type MenuItemSubmenuProps = {
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  children: ReactNode;
  /** Text to search against. If not provided, label text content will be used */
  searchText?: string;
  /** Width of the submenu. If not provided, inherits from parent menu */
  menuWidth?: BaseMenuProps['width'];
  /** Enables search within this submenu */
  searchable?: BaseMenuProps['searchable'];
  /** When set with {@link MenuItemSubmenuProps.onCheckedChange}, shows a checkbox on the trigger row */
  checked?: boolean;
  onCheckedChange?: (next: boolean) => void;
};

export const MenuItemSubmenu: React.FC<MenuItemSubmenuProps> = ({
  label,
  icon,
  disabled,
  children,
  searchText,
  menuWidth,
  searchable = false,
  checked = false,
  onCheckedChange,
}) => {
  const hasCheckbox = onCheckedChange !== undefined;
  const [checkedValue, toggleChecked] = useOptimisticBoolean(
    checked,
    onCheckedChange ?? (() => undefined),
  );

  const checkboxIcon = useMemo(() => {
    if (!hasCheckbox) return undefined;
    return (
      <Box
        component="span"
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled) toggleChecked(!checkedValue);
        }}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Checkbox
          checked={checkedValue}
          disableRipple
          disableTouchRipple
          tabIndex={-1}
          size="small"
          sx={{
            padding: 0,
            pointerEvents: 'none',
            color: 'inherit',
            '&.Mui-checked': { color: 'inherit' },
          }}
        />
      </Box>
    );
  }, [checkedValue, disabled, hasCheckbox, toggleChecked]);

  const itemIndexRef = useRef<number | null>(null);
  const hoverOpenTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const triggerButtonRef = useRef<HTMLDivElement | null>(null);
  const {
    registerSubmenu,
    unregisterSubmenu,
    registerItemVisibility,
    closeAllMenus: closeParentMenus,
    closeOtherSubmenus,
    width,
    submenuSide,
  } = useMenuContext();
  const parentContext = useMenuGroupContext();

  const openSubmenu = useCallback(() => {
    if (disabled || !triggerButtonRef.current) return;
    setAnchor(triggerButtonRef.current);
  }, [disabled]);

  const closeSubmenu = useCallback(() => {
    setAnchor(null);
  }, []);

  const handleItemClick = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    if (disabled) setAnchor(null);
    else if (triggerButtonRef.current && itemIndexRef.current !== null) {
      closeOtherSubmenus(itemIndexRef.current);
      setAnchor(triggerButtonRef.current);
    }
  }, [disabled, closeOtherSubmenus]);

  const handleClose = useCallback(() => setAnchor(null), []);

  const handleCloseAll = useCallback(() => {
    setAnchor(null);
    closeParentMenus();
  }, [closeParentMenus]);

  const handleTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      triggerButtonRef.current = node;
      if (!node && itemIndexRef.current !== null) {
        unregisterSubmenu(itemIndexRef.current);
        itemIndexRef.current = null;
      }
    },
    [unregisterSubmenu],
  );

  const handleItemRegistered = useCallback(
    (index: number, element: HTMLDivElement) => {
      if (itemIndexRef.current !== index) {
        if (itemIndexRef.current !== null) {
          unregisterSubmenu(itemIndexRef.current);
        }
        itemIndexRef.current = index;
        registerSubmenu(index, openSubmenu, closeSubmenu);
        triggerButtonRef.current = element;
      }
    },
    [registerSubmenu, unregisterSubmenu, openSubmenu, closeSubmenu],
  );

  const closeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const triggerDelayedClose = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(closeSubmenu, SUBMENU_TIMEOUTS.CLOSE);
  }, [closeSubmenu]);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    if (disabled || anchor !== null) return;
    clearTimeout(hoverOpenTimeoutRef.current);
    hoverOpenTimeoutRef.current = setTimeout(() => {
      if (itemIndexRef.current === null) return;
      closeOtherSubmenus(itemIndexRef.current);
      openSubmenu();
    }, SUBMENU_TIMEOUTS.OPEN);
  }, [closeOtherSubmenus, openSubmenu, disabled, anchor]);

  const handleMouseDown = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    if (disabled) return;
    clearTimeout(hoverOpenTimeoutRef.current);
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverOpenTimeoutRef.current);
    triggerDelayedClose();
  }, [triggerDelayedClose]);

  // Create context that bubbles up registrations to parent (group) or root
  const submenuContextValue = useMemo<MenuParentContext>(
    () => ({
      registerItemVisibility: (itemIndex: number, isVisible: boolean) => {
        // Bubble up to parent context if it exists, otherwise register with root
        const register = parentContext
          ? parentContext.registerItemVisibility
          : registerItemVisibility;
        register(itemIndex, isVisible);
      },
      registerChildIndex: (index: number) => {
        // Bubble up to parent context if it exists (root doesn't need to track child indices)
        parentContext?.registerChildIndex(index);
      },
      unregisterChildIndex: (index: number) => {
        // Bubble up to parent context if it exists (root doesn't need to track child indices)
        parentContext?.unregisterChildIndex(index);
      },
    }),
    [registerItemVisibility, parentContext],
  );

  const handleSubmenuMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const { currentTarget, relatedTarget } = e;

      if (
        is.HTMLElement(relatedTarget) &&
        currentTarget.contains(relatedTarget)
      ) {
        return; // event originated from inside the submenu - abort
      }

      triggerDelayedClose();
    },
    [triggerDelayedClose],
  );

  const handleSubmenuMouseEnter = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(closeTimeoutRef.current);
      clearTimeout(hoverOpenTimeoutRef.current);
    },
    [],
  );

  return (
    <>
      <MenuGroupContext.Provider value={submenuContextValue}>
        <BaseMenuItem
          ref={handleTriggerRef}
          icon={checkboxIcon ?? icon}
          disabled={disabled}
          highlighted={anchor ? true : undefined}
          closeOnClick={false}
          onClick={handleItemClick}
          onMouseDown={handleMouseDown}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          searchText={searchText}
          onRegistered={handleItemRegistered}
        >
          {label}
          <MenuItemRightIcon>
            <Icon
              category="mui"
              size="small"
              outerSize="medium"
              icon="KeyboardArrowRight"
            />
          </MenuItemRightIcon>
        </BaseMenuItem>
      </MenuGroupContext.Provider>
      {anchor && (
        <BaseMenu
          open={true}
          type="submenu"
          anchorEl={anchor}
          onClose={handleCloseAll}
          onCloseCurrent={handleClose}
          preferredSide={submenuSide}
          width={menuWidth ?? width}
          searchable={searchable}
          onMouseLeave={handleSubmenuMouseLeave}
          onMouseEnter={handleSubmenuMouseEnter}
        >
          {children}
        </BaseMenu>
      )}
    </>
  );
};

MenuItemSubmenu.displayName = 'MenuItemSubmenu';
